import { createContext, useContext, type ReactNode } from "react";
import { formatearMonto } from "@/shared/format/money";

/**
 * Número de sociedad SAP de la organización de la solicitud en curso. Se provee en la raíz de
 * cada página de prorrateo y lo consumen los pickers de centro de costo (`useCecosParaSolicitud`)
 * para ofrecer SOLO los CeCos válidos de esa organización (filtrado estricto CeCo↔organización).
 * `undefined` cuando la organización aún no está derivada → el picker cae al catálogo global y el
 * backend sigue siendo la frontera de validación real.
 */
const SociedadContext = createContext<string | undefined>(undefined);

export function ProrrateoSociedadProvider({
  numeroSociedad,
  moneda,
  children,
}: {
  numeroSociedad?: string;
  /** Moneda de los importes (ver `ProrrateoMonedaProvider`). */
  moneda?: string | null;
  children: ReactNode;
}) {
  return (
    <SociedadContext.Provider value={numeroSociedad}>
      <MonedaContext.Provider value={moneda ?? undefined}>{children}</MonedaContext.Provider>
    </SociedadContext.Provider>
  );
}

export function useSociedadDeProrrateo(): string | undefined {
  return useContext(SociedadContext);
}

/**
 * Moneda de los importes del prorrateo en curso (la del CFDI / Invoice / documento de compra)
 * · la muestran los inputs y textos de importe. En Múltiples CFDI cada sección provee la de
 * su CFDI. `undefined` → el importe se muestra sin moneda.
 */
const MonedaContext = createContext<string | undefined>(undefined);

export function ProrrateoMonedaProvider({ moneda, children }: { moneda?: string | null; children: ReactNode }) {
  return <MonedaContext.Provider value={moneda ?? undefined}>{children}</MonedaContext.Provider>;
}

export function useMonedaDeProrrateo(): string | undefined {
  return useContext(MonedaContext);
}

/** Formateador de importes del prorrateo con su moneda («MXN 22,500.00»). */
export function useFormatoImporte(): (valor: number) => string {
  const moneda = useMonedaDeProrrateo();
  return (valor) => formatearMonto(valor, moneda, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
